import { Component, computed, OnInit, OnDestroy, signal } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { MessagesModule } from 'primeng/messages';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { RadioButtonModule } from 'primeng/radiobutton'; // <-- Importamos RadioButton
import { Subscription } from 'rxjs';

import { WaitDaysForDueDateConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { CampaignContextService } from '@shared/services/campaign-context.service';
import { CampaignModel } from '@modules/campaigns/models/campaign.model';
import { RegionalConfigInfoComponent } from '@shared/components/regional-config-info/regional-config-info.component';
import { WaitDaysForDueDateNode } from '@modules/campaigns/Domain/Nodes/WaitDaysOfExpirationNode';

@Component({
  selector: 'app-wait-days-for-due-date-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    LucideIconsModule,
    InputNumberModule,
    DropdownModule,
    MessagesModule,
    CardModule,
    TooltipModule,
    NgClass,
    RadioButtonModule,
    RegionalConfigInfoComponent,
  ],
  templateUrl: './wait-days-for-due-date-inspector.component.html',
  styleUrls: ['./wait-days-for-due-date-inspector.component.scss'],
})
export class WaitDaysForDueDateInspectorComponent
  extends InspectorBase<WaitDaysForDueDateNode>
  implements OnInit, OnDestroy
{
  waitDays = signal<number | null>(0);
  partDay = signal<string | null>('MORNING');
  antesODespues = signal<string | null>('beforeDueDate');
  campaignSelected = signal<CampaignModel | null>(null);

  private campaignSub?: Subscription;

  partDayOptions = computed(() => [
    { label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING'), value: 'MORNING' },
    { label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING'), value: 'EVENING' },
    { label: this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY'), value: 'FULL_DAY' },
  ]);

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
    private campaignContext: CampaignContextService,
  ) {
    super();
  }

  ngOnInit() {
    this.campaignSub = this.campaignContext.campaign$.subscribe((campaign) => {
      if (campaign) {
        this.campaignSelected.set(campaign);
      }
    });
  }

  ngOnDestroy() {
    this.campaignSub?.unsubscribe();
  }

  override get nodeValue(): WaitDaysForDueDateNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.WaitDaysForDueDate
      ? (current as WaitDaysForDueDateNode)
      : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant(
          'DATA.MODULES.INSPECTORS.WAIT_DAYS_FOR_DUE_DATE.TABS.PREVIEW',
        ),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAYS_FOR_DUE_DATE.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  onPartDayChange(value: string) {
    this.partDay.set(value);
    this.scheduleApplyChanges();
  }

  onWaitDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 0;
    if (value < 0) value = 0;
    this.waitDays.set(value);
    this.scheduleApplyChanges();
  }

  onDirectionChange(value: string) {
    this.antesODespues.set(value);
    this.scheduleApplyChanges();
  }

  getSummaryMessage(): string {
    const partDay = this.getPartDayLabel().toLowerCase();
    const waitDays = this.waitDays() ?? 0;
    const direction =
      this.antesODespues() === 'beforeDueDate'
        ? this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAYS_FOR_DUE_DATE.PREVIEW.BEFORE')
        : this.translate.instant('DATA.MODULES.INSPECTORS.WAIT_DAYS_FOR_DUE_DATE.PREVIEW.AFTER');

    return this.translate.instant(
      'DATA.MODULES.INSPECTORS.WAIT_DAYS_FOR_DUE_DATE.PREVIEW.SUMMARY_MESSAGE',
      {
        partDay,
        waitDays,
        antesODespues: direction,
      },
    );
  }

  private getPartDayLabel(): string {
    const partDay = this.partDay();
    if (partDay === 'MORNING') return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.MORNING');
    if (partDay === 'EVENING') return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.EVENING');
    if (partDay === 'FULL_DAY') return this.translate.instant('DATA.COMMON.TIME_OF_WEEK.FULL_DAY');
    return '-';
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig: WaitDaysForDueDateConfig = {
      ...(node.config as WaitDaysForDueDateConfig),
      partDay: this.partDay() || null,
      waitDays: this.waitDays() ?? 0,
      antesODespues: this.antesODespues() || null,
    };

    const updated = new WaitDaysForDueDateNode(node.id, node.position, updatedConfig);
    updated.parentNodes = [...node.parentNodes];
    updated.childrenNodes = [...node.childrenNodes];

    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as any;

    this.partDay.set(cfg.partDay ?? 'MORNING');

    let days = cfg.waitDays ?? 0;
    let direction = cfg.antesODespues ?? 'afterDueDate';

    if (days < 0 || cfg.antesODespues === 'beforeDueDate' || cfg.antesODespues === 'BEFORE') {
      days = Math.abs(days);
      direction = 'beforeDueDate';
    } else if (cfg.antesODespues === 'afterDueDate' || cfg.antesODespues === 'AFTER') {
      direction = 'afterDueDate';
    }

    this.waitDays.set(days);
    this.antesODespues.set(direction);

    this.errors.set([]);
  }
}
